import { bindPages, canList, marketFormat, show } from '@makan/command-center/kit';
import type { CampaignRow, OfferRow } from '../domain/index.ts';
import { routes } from '../domain/values.ts';
import { t } from './strings.ts';

/**
 * crm's ui surface entry, read by the command center's generated
 * features.gen.ts: a lazy page per route of crm's values entry, so the
 * public join page never downloads the staff page. A route without a page,
 * or a page without a route, does not compile. Its staff pages sit in the
 * sidebar after Home; the campaigns only for staff who may list them. The
 * counter (redeem) is a restaurant screen the kiosk will take over: reached
 * by its address, not the CRM menu.
 */
/** The menu's figures are written in the default market's way: the frame draws before a page reads its group's market. */
const format = marketFormat();
const count = (value: number | undefined) => (value === undefined ? null : format.count(value));

async function rowsOf(groupId: string, projection: string, query: Readonly<Record<string, number>> = { limit: 500 }): Promise<readonly object[]> {
  const answer = await show(groupId, 'crm', projection, query);
  return answer.outcome === 'rows' ? answer.rows : [];
}

/** Open campaigns, as "3 live": not ended by hand and not past their last day (today in the default market). */
function live(campaigns: readonly CampaignRow[]): string {
  const today = format.today();
  return t.campaigns.live(campaigns.filter((row) => row.endedAt === null && row.campaign.entry.from <= today && (row.campaign.entry.until === null || row.campaign.entry.until >= today)).length, format.count);
}

export const ui = bindPages(
  routes,
  {
    join: () => import('./join.tsx'),
    members: () => import('./members.tsx'),
    member: () => import('./member.tsx'),
    privacy: () => import('./privacy.tsx'),
    redeem: () => import('./redeem.tsx'),
    campaigns: () => import('./campaigns.tsx'),
    vouchers: () => import('./catalogue.tsx'),
    voucherStation: () => import('./voucher-station.tsx'),
  },
  {
    members: { label: t.members.title, icon: 'people', rank: 10, count: async (groupId) => count(((await rowsOf(groupId, 'member-count', {}))[0] as { total?: number } | undefined)?.total) },
    campaigns: { label: t.campaigns.title, icon: 'megaphone', rank: 30, shows: (staff) => canList(staff, 'crm', 'campaigns'), count: async (groupId) => live((await rowsOf(groupId, 'campaigns')) as readonly CampaignRow[]) },
    vouchers: { label: t.catalogue.title, icon: 'ticket', rank: 40, shows: (staff) => canList(staff, 'crm', 'offers'), count: async (groupId) => count(((await rowsOf(groupId, 'offers')) as readonly OfferRow[]).filter((offer) => offer.archivedAt === null).length) },
  },
);
